import { useParams, useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Row,
  Col,
  Card,
  Table,
  InputGroup,
} from "react-bootstrap";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import Loader from "../../Components/Loader.jsx";
import Message from "../../Components/Message.jsx";
import {
  useAdminGetQuoteQuery,
  useAdminCreateQuoteMutation,
  useAdminSendQuoteEmailMutation,
  useAdminUpdateQuoteMutation,
} from "../../slices/quotesApiSlice";
import {
  useAdminListPricingTiersQuery,
  useAdminLookupTierPricesForProductsMutation,
} from "../../slices/partnerCodesApiSlice";
import { useGetProductsQuery } from "../../slices/productsApiSlice"; // already in your project
import BackButton from "../../Components/BackButton";
import ProductLocationsSummary, {
  formatProductLocationsText,
} from "../../Components/admin/ProductLocationsSummary";
import { getEffectivePrice } from "../../utils/salePricing";
import { getTotalItemQuantity } from "../../utils/itemCount";

const STATUS_OPTIONS = [
  "DRAFT",
  "SENT",
  "ISSUED",
  "ACCEPTED",
  "DECLINED",
  "EXPIRED",
];
const TAX_PROVINCES = [
  { code: "AB", label: "Alberta", rate: 0.05 },
  { code: "BC", label: "British Columbia", rate: 0.12 },
  { code: "MB", label: "Manitoba", rate: 0.12 },
  { code: "NB", label: "New Brunswick", rate: 0.15 },
  { code: "NL", label: "Newfoundland and Labrador", rate: 0.15 },
  { code: "NT", label: "Northwest Territories", rate: 0.05 },
  { code: "NS", label: "Nova Scotia", rate: 0.15 },
  { code: "NU", label: "Nunavut", rate: 0.05 },
  { code: "ON", label: "Ontario", rate: 0.13 },
  { code: "PE", label: "Prince Edward Island", rate: 0.15 },
  { code: "QC", label: "Quebec", rate: 0.14975 },
  { code: "SK", label: "Saskatchewan", rate: 0.11 },
  { code: "YT", label: "Yukon", rate: 0.05 },
];
const formatInterchangeables = (interchangeables) => {
  if (!interchangeables) return "";
  if (typeof interchangeables === "string") return interchangeables;
  if (!Array.isArray(interchangeables)) return "";
  const parts = interchangeables
    .map((entry) => {
      if (!entry) return "";
      if (typeof entry === "string") return entry;
      const label = entry.brandLabel ? String(entry.brandLabel).trim() : "";
      const part = entry.partNumber ? String(entry.partNumber).trim() : "";
      return [label, part].filter(Boolean).join(" ");
    })
    .filter(Boolean);
  return parts.join(", ");
};

const mapQuoteItem = (i = {}) => {
  const item = {
    product: i.product || null,
    name: i.displayName || i.name,
    displayName: i.displayName || i.name,
    sku: i.sku || "",
    oemNumbers: i.oemNumbers || i.partNumber || "",
    interchangeables: i.interchangeables || "",
    qty: Number(i.qty) || 1,
    price: Number(i.price) || 0,
    originalPrice: Number(i.originalPrice ?? i.price) || 0,
    priceManuallySet: false,
    inventoryLocations: Array.isArray(i.inventoryLocations)
      ? i.inventoryLocations
      : [],
    location: i.location || "",
    locationText: i.locationText || i.pickLocations || "",
  };
  return {
    ...item,
    locationText: item.locationText || formatProductLocationsText(item),
  };
};

const getProductIdentity = (item = {}) => ({
  productId: item.product || item._id ? String(item.product || item._id) : "",
  sku:
    item.sku || item.SKU || item.partNumber
      ? String(item.sku || item.SKU || item.partNumber)
          .trim()
          .toLowerCase()
      : "",
});

const QuoteEditScreen = () => {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const {
    data: quote,
    isLoading,
    error,
    refetch,
  } = useAdminGetQuoteQuery(id, {
    skip: isNew,
  });
  const [createQuote, { isLoading: creating }] = useAdminCreateQuoteMutation();
  const [updateQuote, { isLoading: saving }] = useAdminUpdateQuoteMutation();
  const [sendQuoteEmail, { isLoading: sendingEmail }] =
    useAdminSendQuoteEmailMutation();
  const { data: pricingTiers = [] } = useAdminListPricingTiersQuery();
  const [lookupTierPrices] = useAdminLookupTierPricesForProductsMutation();

  // local editable items state
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [discountValue, setDiscountValue] = useState(0);
  const [taxProvince, setTaxProvince] = useState("ON");
  const [selectedTierId, setSelectedTierId] = useState("");
  const [tierPricesByProductId, setTierPricesByProductId] = useState({});
  const addProductLocksRef = useRef(new Set());
  const { data: productResults } = useGetProductsQuery(
    { keyword: search, pageNumber: 1 },
    { skip: !search || search.length < 2 },
  );

  // initialize items when quote loads
  useEffect(() => {
    if (quote?.items) {
      setItems(quote.items.map(mapQuoteItem));
      setDiscountValue(Number(quote?.discount ?? 0));
      const taxableSubtotal = Math.max(
        0,
        Number(quote?.subtotal ?? 0) - Number(quote?.discount ?? 0),
      );
      if (quote?.taxProvince) {
        setTaxProvince(String(quote.taxProvince).toUpperCase());
      } else if (taxableSubtotal > 0) {
        const existingRate =
          Number.isFinite(Number(quote?.taxRate)) && Number(quote.taxRate) >= 0
            ? Number(quote.taxRate)
            : Number(quote?.tax ?? 0) / taxableSubtotal;
        const closest = TAX_PROVINCES.reduce((best, province) => {
          if (!best) return province;
          return Math.abs(province.rate - existingRate) <
            Math.abs(best.rate - existingRate)
            ? province
            : best;
        }, null);
        if (closest?.code) setTaxProvince(closest.code);
      } else {
        setTaxProvince("ON");
      }
    } else if (isNew) {
      setItems([]);
      setDiscountValue(0);
      setTaxProvince("ON");
    }
  }, [quote, isNew]);

  const productIdsForLookup = useMemo(() => {
    const ids = new Set();
    items.forEach((item) => {
      if (item?.product) ids.add(String(item.product));
    });
    productResults?.products?.forEach((product) => {
      if (product?._id) ids.add(String(product._id));
    });
    return Array.from(ids);
  }, [items, productResults]);

  const tierLookupKey = useMemo(
    () => productIdsForLookup.join("|"),
    [productIdsForLookup],
  );

  useEffect(() => {
    let isActive = true;
    if (!selectedTierId) {
      setTierPricesByProductId({});
      return undefined;
    }
    if (productIdsForLookup.length === 0) {
      setTierPricesByProductId({});
      return undefined;
    }

    lookupTierPrices({
      tierId: selectedTierId,
      productIds: productIdsForLookup,
    })
      .unwrap()
      .then((res) => {
        if (!isActive) return;
        const map = {};
        (res?.prices || []).forEach((entry) => {
          if (entry?.productId) {
            map[String(entry.productId)] = Number(entry.price);
          }
        });
        setTierPricesByProductId(map);
      })
      .catch((err) => {
        if (!isActive) return;
        toast.error(err?.data?.message || "Unable to load tier pricing.");
        setTierPricesByProductId({});
      });

    return () => {
      isActive = false;
    };
  }, [lookupTierPrices, productIdsForLookup, selectedTierId, tierLookupKey]);

  useEffect(() => {
    if (!selectedTierId) {
      return;
    }
    setItems((prev) =>
      prev.map((item) => {
        const productId = item?.product ? String(item.product) : null;
        if (!productId || item.priceManuallySet) return item;
        const tierPrice = tierPricesByProductId[productId];
        const originalPrice = Number(item.originalPrice ?? item.price ?? 0);
        if (!Number.isFinite(originalPrice)) return item;

        if (tierPrice == null) {
          return Number(item.price) === originalPrice
            ? item
            : { ...item, price: originalPrice, originalPrice };
        }

        const nextPrice = Number(tierPrice);
        if (!Number.isFinite(nextPrice)) return item;
        if (
          Number(item.price) === nextPrice &&
          item.originalPrice === originalPrice
        ) {
          return item;
        }
        return { ...item, price: nextPrice, originalPrice };
      }),
    );
  }, [selectedTierId, tierPricesByProductId]);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        id,
        customerName: e.target.customerName.value,
        customerEmail: e.target.customerEmail.value,
        customerPhone: e.target.customerPhone.value,
        billingAddress: e.target.billingAddress.value,
        status: e.target.status.value,
        items: items.map(({ priceManuallySet, ...rest }) => rest),
        discount: discountValue,
        taxProvince,

        // NEW FIELDS THAT FIX THE PDF ISSUE:
        subtotal: computedSubtotal,
        tax: computedTax,
        taxRate: TAX_RATE,
        total: computedTotal,
      };
      if (isNew) {
        const created = await createQuote(payload).unwrap();
        toast.success("Quote created");
        navigate(`/admin/quotes/${created._id}/edit`);
        return;
      }
      const updated = await updateQuote(payload).unwrap();
      // stay on page: refresh local state with server truth
      setItems((updated?.items || []).map(mapQuoteItem));
      setDiscountValue(Number(updated?.discount ?? discountValue));
      setTaxValue(Number(updated?.tax ?? 0)); // now shows the server-computed HST
      toast.success("Quote saved");
      await refetch(); // refresh header totals/status
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const addItemFromProduct = (p) => {
    const incoming = getProductIdentity(p);
    const addLockKey = incoming.productId || incoming.sku;
    if (addLockKey && addProductLocksRef.current.has(addLockKey)) {
      return;
    }
    if (addLockKey) {
      addProductLocksRef.current.add(addLockKey);
      setTimeout(() => {
        addProductLocksRef.current.delete(addLockKey);
      }, 300);
    }

    const alreadyAdded = items.some((item) => {
      const existing = getProductIdentity(item);
      if (incoming.productId && existing.productId) {
        return incoming.productId === existing.productId;
      }
      return Boolean(
        incoming.sku && existing.sku && incoming.sku === existing.sku,
      );
    });

    if (alreadyAdded) {
      toast.info("Product already added");
      return;
    }

    const listPrice = Number(p?.price ?? p?.listPrice ?? 0);
    const salePrice = Number(p?.salePrice ?? 0);
    const tierPrice =
      selectedTierId && p?._id ? tierPricesByProductId[String(p._id)] : null;
    const unit = selectedTierId
      ? (tierPrice ?? listPrice)
      : getEffectivePrice({
          price: listPrice,
          salePrice,
          saleStartAt: p?.saleStartAt,
          saleEndAt: p?.saleEndAt,
        });
    const locationSnapshot = formatProductLocationsText(p);

    setItems((prev) => [
      ...prev,
      {
        product: p?._id ?? null,
        name: p?.displayName || p?.name || "(Unnamed)",
        displayName: p?.displayName || p?.name || "(Unnamed)",
        sku: p?.sku || p?.SKU || p?.partNumber || "",
        oemNumbers:
          Array.isArray(p?.oemNumbers) && p.oemNumbers.length > 0
            ? p.oemNumbers.join(", ")
            : p?.oemNumbers || p?.partNumber || "",
        interchangeables: formatInterchangeables(
          p?.interchangeables || p?.interchangeable,
        ),
        qty: 1,
        price: isFinite(unit) ? unit : 0,
        originalPrice: isFinite(listPrice)
          ? listPrice
          : isFinite(unit)
            ? unit
            : 0,
        priceManuallySet: false,
        inventoryLocations: Array.isArray(p?.inventoryLocations)
          ? p.inventoryLocations
          : [],
        location: p?.location || "",
        locationText: locationSnapshot,
      },
    ]);
    setSearch("");
    toast.success("Item added");
  };

  const updateQty = (idx, val) => {
    setItems((prev) =>
      prev.map((it, i) =>
        i === idx ? { ...it, qty: Math.max(1, Number(val) || 1) } : it,
      ),
    );
  };
  const updatePrice = (idx, val) => {
    setItems((prev) =>
      prev.map((it, i) =>
        i === idx
          ? {
              ...it,
              price: Math.max(0, Number(val) || 0),
              priceManuallySet: true,
            }
          : it,
      ),
    );
  };
  const removeItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const selectedTaxRegion = useMemo(
    () =>
      TAX_PROVINCES.find((province) => province.code === taxProvince) ||
      TAX_PROVINCES.find((province) => province.code === "ON"),
    [taxProvince],
  );
  const TAX_RATE = selectedTaxRegion?.rate ?? 0.13;

  // Recalculate tax whenever items change
  const computedSubtotal = items.reduce((s, i) => s + i.qty * i.price, 0);
  const totalItems = getTotalItemQuantity(items);
  const safeDiscount = Math.max(0, Number(discountValue) || 0);
  const taxableSubtotal = Math.max(0, computedSubtotal - safeDiscount);
  const computedTax = +(taxableSubtotal * TAX_RATE).toFixed(2);
  const computedTotal = +(taxableSubtotal + computedTax).toFixed(2);

  // Local tax state (shown in the form)
  const [taxValue, setTaxValue] = useState(0);
  const [printPickList, setPrintPickList] = useState(false);

  // When quote loads, initialize tax from DB but override with computed if items change
  useEffect(() => {
    if (quote) {
      setTaxValue(computedTax);
    }
  }, [quote, computedTax]);

  if (isLoading) return <Loader />;
  if (error)
    return (
      <Message variant="danger">{error?.data?.message || error.error}</Message>
    );

  const openPdf = async () => {
    if (isNew) {
      toast.error("Save the quote before opening the PDF.");
      return;
    }
    try {
      const res = await fetch(`/api/quotes/${id}/pdf`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      // Optional: URL.revokeObjectURL(url) later
    } catch (e) {
      toast.error("Failed to open PDF");
    }
  };

  const sendEmailHandler = async () => {
    if (isNew) {
      toast.error("Save the quote before sending an email.");
      return;
    }
    try {
      await sendQuoteEmail(id).unwrap();
      toast.success("Quote email sent.");
    } catch (err) {
      toast.error(
        err?.data?.message || err?.message || "Failed to send quote email.",
      );
    }
  };

  const printPickListHandler = () => {
    setPrintPickList(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintPickList(false), 300);
    }, 50);
  };

  return (
    <>
      <BackButton to={isNew ? "/admin/quotes" : undefined} />
      <Row className="mb-3">
        <Col>
          <h1>{isNew ? "New Quote" : `Edit Quote ${quote?.number}`}</h1>
        </Col>
        <Col className="text-end">
          <div className="d-flex justify-content-end gap-2">
            {!isNew && quote?.orderRef && (
              <Button
                variant="info"
                onClick={() => navigate(`/order/${quote.orderRef}`)}
              >
                View Order
              </Button>
            )}
            {!isNew && !quote?.orderRef && (
              <Button
                variant="info"
                disabled={["DECLINED", "EXPIRED"].includes(quote?.status)}
                onClick={() => navigate(`/admin/quotes/${id}/convert`)}
              >
                Create Order
              </Button>
            )}
            <Button
              variant="outline-secondary"
              onClick={openPdf}
              disabled={isNew}
            >
              Print / PDF
            </Button>
            <Button variant="outline-dark" onClick={printPickListHandler}>
              Print Pick List
            </Button>
            <Button
              variant="primary"
              onClick={sendEmailHandler}
              disabled={sendingEmail || isNew}
            >
              {sendingEmail ? "Sending…" : "Send Quote Email"}
            </Button>
          </div>
        </Col>
      </Row>
      <Form onSubmit={onSubmit}>
        <style>{`
          @media print {
            body * { visibility: hidden; }
            .pick-list-print, .pick-list-print * { visibility: visible; }
            .pick-list-print { position: absolute; left: 0; top: 0; width: 100%; }
          }
        `}</style>
        {printPickList && (
          <div className="pick-list-print p-3">
            <h2 className="mb-2">Quote Pick List</h2>
            <div>
              <strong>Quote #:</strong>{" "}
              {quote?.number || quote?.quoteNumber || id}
            </div>
            <div>
              <strong>Customer:</strong> {quote?.customerName || "-"}
            </div>
            <div>
              <strong>Date:</strong>{" "}
              {quote?.createdAt
                ? new Date(quote.createdAt).toLocaleDateString()
                : "-"}
            </div>
            <Table bordered size="sm" className="mt-3">
              <thead>
                <tr>
                  <th style={{ width: 50 }}>#</th>
                  <th>Product</th>
                  <th>SKU</th>
                  <th>OEM</th>
                  <th>Ordered Qty</th>
                  <th>Pick Locations</th>
                </tr>
              </thead>
              <tbody>
                {(items || []).map((it, idx) => (
                  <tr key={`pick-${idx}`}>
                    <td>{idx + 1}</td>
                    <td>{it.displayName || it.name || ""}</td>
                    <td>{it.sku || "-"}</td>
                    <td>{it.oemNumbers || it.partNumber || "-"}</td>
                    <td>{Math.max(1, Number(it.qty) || 1)}</td>
                    <td>
                      <ProductLocationsSummary product={it} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        <Row>
          <Col md={6}>
            <Card className="mb-3 p-3">
              <Form.Group className="mb-2">
                <Form.Label>Customer Name</Form.Label>
                <Form.Control
                  name="customerName"
                  defaultValue={quote?.customerName || ""}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  name="customerEmail"
                  type="email"
                  defaultValue={quote?.customerEmail || ""}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  name="customerPhone"
                  defaultValue={quote?.customerPhone || ""}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Billing Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="billingAddress"
                  placeholder="Street, City, Province/State, Postal Code, Country"
                  defaultValue={quote?.billingAddress || ""}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  defaultValue={quote?.status || "DRAFT"}
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Pricing Tier</Form.Label>
                <Form.Select
                  value={selectedTierId}
                  onChange={(e) => setSelectedTierId(e.target.value)}
                >
                  <option value="">No tier pricing</option>
                  {pricingTiers.map((tier) => (
                    <option key={tier._id} value={tier._id}>
                      {tier.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Tax Province</Form.Label>
                <Form.Select
                  name="taxProvince"
                  value={taxProvince}
                  onChange={(e) => setTaxProvince(e.target.value)}
                >
                  {TAX_PROVINCES.map((province) => (
                    <option key={province.code} value={province.code}>
                      {province.label} (
                      {(province.rate * 100).toFixed(3).replace(/\.?0+$/, "")}%)
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Discount (CAD)</Form.Label>
                <Form.Control
                  name="discount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>
                  Tax (CAD) — auto (
                  {(TAX_RATE * 100).toFixed(3).replace(/\.?0+$/, "")}%)
                </Form.Label>
                <Form.Control
                  name="tax"
                  type="number"
                  step="0.01"
                  value={taxValue}
                  readOnly
                />
              </Form.Group>
              <div className="d-flex flex-wrap gap-2">
                <Button type="submit" disabled={saving || creating}>
                  {isNew ? "Create" : "Save"}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate("/admin/quotes")}
                >
                  Save & Close
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => navigate("/admin/quotes")}
                >
                  Cancel
                </Button>
              </div>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="mb-3 p-3">
              <div>
                <strong>Status:</strong> {quote?.status}
              </div>
              <div>
                <strong>Subtotal:</strong> {computedSubtotal.toFixed(2)}
              </div>
              <div>
                <strong>Discount:</strong> {safeDiscount.toFixed(2)}
              </div>
              <div>
                <strong>Tax Province:</strong>{" "}
                {selectedTaxRegion?.label || "Ontario"}
              </div>
              <div>
                <strong>Tax:</strong> {computedTax.toFixed(2)}
              </div>
              <div>
                <strong>Total Items:</strong> {totalItems}
              </div>
              <div>
                <strong>Total:</strong> {computedTotal.toFixed(2)}
              </div>
            </Card>
          </Col>
        </Row>

        <Card className="p-3">
          <h4 className="mb-3">Items</h4>
          <InputGroup className="mb-3">
            <Form.Control
              placeholder="Search products by name, SKU, or interchangeables…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
          {search && productResults?.products?.length > 0 && (
            <Table
              size="sm"
              bordered
              hover
              striped
              className="mb-3 align-middle"
            >
              <thead>
                <tr>
                  <th>Product</th>
                  <th>OEM</th>
                  <th>Interchangeable</th>
                  <th>Price</th>
                  <th>Locations</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {productResults.products.map((p) => {
                  const interchangeableLabel = formatInterchangeables(
                    p?.interchangeables || p?.interchangeable,
                  );
                  const listPrice = Number(p?.price ?? p?.listPrice ?? 0);
                  const salePrice = Number(p?.salePrice ?? 0);
                  const tierPrice =
                    selectedTierId && p?._id
                      ? tierPricesByProductId[String(p._id)]
                      : null;
                  const displayPrice = selectedTierId
                    ? (tierPrice ?? listPrice)
                    : getEffectivePrice({
                        price: listPrice,
                        salePrice,
                        saleStartAt: p?.saleStartAt,
                        saleEndAt: p?.saleEndAt,
                      });
                  return (
                    <tr
                      key={p._id}
                      className="quote-add-row"
                      role="button"
                      tabIndex={0}
                      onClick={() => addItemFromProduct(p)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          addItemFromProduct(p);
                        }
                      }}
                    >
                      <td>{p.name}</td>
                      <td>
                        {Array.isArray(p.oemNumbers) && p.oemNumbers.length > 0
                          ? p.oemNumbers.join(", ")
                          : p.oemNumbers || "-"}
                      </td>
                      <td>{interchangeableLabel || "-"}</td>
                      <td>{Number(displayPrice).toFixed(2)}</td>
                      <td>
                        <ProductLocationsSummary product={p} />
                      </td>
                      <td className="text-end">
                        <Button
                          type="button"
                          size="sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            addItemFromProduct(p);
                          }}
                        >
                          Add
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}

          <Table
            bordered
            hover
            striped
            responsive
            className="table-sm align-middle"
          >
            <thead>
              <tr>
                <th style={{ width: 50 }}>#</th>
                <th>Product/Name</th>
                <th>OEM</th>
                <th>Interchangeable</th>
                <th style={{ width: 120 }}>Qty</th>
                <th style={{ width: 160 }}>Unit (CAD)</th>
                <th style={{ width: 160 }}>Line Total</th>
                <th>Locations</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(items || []).map((it, idx) => {
                const qty = Math.max(1, Number(it.qty) || 1);
                const price = Math.max(0, Number(it.price) || 0);
                const line = qty * price;
                return (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{it.displayName || it.name || ""}</td>
                    <td>{it.oemNumbers || it.partNumber || it.sku || ""}</td>
                    <td>{it.interchangeables || "-"}</td>
                    <td>
                      <Form.Control
                        type="number"
                        min="1"
                        value={qty}
                        onChange={(e) => updateQty(idx, e.target.value)}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={price}
                        onChange={(e) => updatePrice(idx, e.target.value)}
                      />
                    </td>
                    <td>{line.toFixed(2)}</td>
                    <td>
                      <ProductLocationsSummary product={it} />
                    </td>
                    <td className="text-end">
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => removeItem(idx)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-muted">
                    No items yet. Search and add products above.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card>
      </Form>
    </>
  );
};

export default QuoteEditScreen;
